import { VideoLoader } from './VideoLoader';

export interface SimpleFrame {
  imageData: ImageData;
  timestamp: number;
  index: number;
  keypoints?: any; // Optional keypoints from backend
  pedalTag?: 'BDC' | 'TDC' | null; // Tag for pedal position (Bottom/Top Dead Center)
}

export interface AngleData {
  knee: number;   // degrees
  hip: number;    // degrees
  trunk: number;  // degrees from horizontal (0° = aero, 90° = upright)
  elbow: number;  // degrees (180° = straight arm)
}

export interface PedalAnalysis {
  position: 'BDC' | 'TDC';
  averagedKeypoints: any;  // averaged keypoint positions
  angles: AngleData;
  representativeFrameIndex: number;  // index in frames array
}

export interface PelvicRockResult {
  mm: number | null;
  status: 'green' | 'amber' | 'red' | 'no_data';
}

export interface SimpleProcessingResult {
  sideFrames: SimpleFrame[];
  frontFrames: SimpleFrame[];
  sessionId: string;
  pedalAnalysis?: {
    bdc: PedalAnalysis | null;
    tdc: PedalAnalysis | null;
    trunkAngleAvg: number | null;       // average of BDC and TDC trunk angles
    trunkAngleDivergence: boolean;      // true if BDC and TDC trunk angles differ by > 5°
    pelvicRock: PelvicRockResult | null;
  };
}

export class ProcessingPipeline {
  private videoLoader: VideoLoader;

  constructor() {
    this.videoLoader = new VideoLoader();
  }

  /**
   * Process videos - extract frames only.
   *
   * @param getToken - Clerk token getter (from useAuth().getToken).
   *   REQUIRED: the /api/detect-pose endpoint enforces auth.
   *   Forgetting to pass this will cause all pose detections to fail silently
   *   and no skeletons will be drawn on the frames.
   */
  async processVideos(
    sideVideo: File | null,
    frontVideo: File | null,
    progressCallback: (progress: number, stage: string) => void,
    getToken?: () => Promise<string | null>
  ): Promise<SimpleProcessingResult> {
    // Generate unique session ID for this processing run
    const sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
    
    const sideFrames: SimpleFrame[] = [];
    const frontFrames: SimpleFrame[] = [];
    
    try {
      // Validate inputs
      if (!sideVideo && !frontVideo) {
        throw new Error('At least one video (side or front view) is required for processing');
      }

      // Process side view
      if (sideVideo) {
        progressCallback(0, 'Loading side view video...');
        const sideVideoData = await this.videoLoader.loadVideo(sideVideo, 'side');
        
        progressCallback(10, 'Extracting frames from side view...');
        const frames = await this.videoLoader.extractFrames(sideVideoData.videoElement!, 40);
        sideFrames.push(...frames);
        
        // Detect pose on side view frames
        progressCallback(25, 'Detecting body points on side view...');
        const keypointsArray = await this.detectPoseOnFrames(sideFrames, sessionId, getToken);
        
        // Attach keypoints to frames
        sideFrames.forEach((frame, idx) => {
          frame.keypoints = keypointsArray[idx];
        });
        
        // Identify BDC/TDC frames based on ankle positions
        progressCallback(40, 'Identifying pedal positions...');
        this.identifyPedalPositions(sideFrames);
        
        // Debug: Log frames with tags
        const taggedFrames = sideFrames.filter(f => f.pedalTag);
        console.log(`Tagged ${taggedFrames.length} frames:`, taggedFrames.map((f, idx) => ({
          index: sideFrames.indexOf(f),
          tag: f.pedalTag,
          ankleY: f.keypoints?.rightAnkle?.y
        })));
        
        // Cleanup
        if (sideVideoData.videoUrl) {
          URL.revokeObjectURL(sideVideoData.videoUrl);
        }
      }

      // Analyze pedal positions and calculate angles (only if side view was processed)
      let pedalAnalysis = undefined;
      if (sideFrames.length > 0) {
        progressCallback(50, 'Calculating angles...');
        pedalAnalysis = this.analyzePedalPositions(sideFrames);
      }

      // Process front view
      if (frontVideo) {
        progressCallback(60, 'Loading front view video...');
        const frontVideoData = await this.videoLoader.loadVideo(frontVideo, 'front');
        
        progressCallback(70, 'Extracting frames from front view...');
        const frames = await this.videoLoader.extractFrames(frontVideoData.videoElement!, 40);
        frontFrames.push(...frames);
        
        // Cleanup
        if (frontVideoData.videoUrl) {
          URL.revokeObjectURL(frontVideoData.videoUrl);
        }
      }

      progressCallback(100, 'Complete!');

      return {
        sideFrames,
        frontFrames,
        sessionId,
        pedalAnalysis
      };
    } catch (error) {
      console.error('Processing error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Processing failed: ${error}`);
    }
  }

  /**
   * Detect pose on frames using backend.
   * Works with or without auth — the endpoint accepts anonymous requests.
   * When a token is available it is sent for authenticated access.
   */
  private async detectPoseOnFrames(
    frames: SimpleFrame[],
    sessionId: string,
    getToken?: () => Promise<string | null>
  ): Promise<any[]> {
    const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5000';

    // Resolve token with a timeout — don't hang if auth server is unreachable
    let token: string | null = null;
    if (getToken) {
      try {
        token = await Promise.race([
          getToken(),
          new Promise<null>(resolve => setTimeout(() => resolve(null), 3000)),
        ]);
      } catch {
        token = null;
      }
    }

    // Prepare frames for backend
    const framesToSend = frames.map(frame => ({
      index: frame.index,
      image_data: this.imageDataToBase64(frame.imageData),
      timestamp: frame.timestamp
    }));

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${backendUrl}/api/detect-pose`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          session_id: sessionId,
          frames: framesToSend
        })
      });
      
      if (response.status === 401 || response.status === 403) {
        console.error(
          `Pose detection failed: server returned ${response.status}. ` +
          'Check that the user is authenticated and has purchased AI Bike Fitting.'
        );
        return frames.map(() => null);
      }

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        console.error(`Pose detection failed: server returned ${response.status}. ${body}`);
        return frames.map(() => null);
      }
      
      const result = await response.json();
      return result.keypoints;
    } catch (error) {
      console.error('Pose detection failed (network error):', error);
      return frames.map(() => null);
    }
  }

  /**
   * Convert ImageData to base64 string
   */
  private imageDataToBase64(imageData: ImageData): string {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  }

  /**
   * Identify pedal positions (BDC/TDC) based on ankle Y coordinates
   */
  private identifyPedalPositions(frames: SimpleFrame[]): SimpleFrame[] {
    const THRESHOLD = 10; // pixels
    
    // Extract valid ankle Y coordinates
    const ankleData = frames.map((frame, idx) => ({
      index: idx,
      y: frame.keypoints?.rightAnkle?.y,
      confidence: frame.keypoints?.rightAnkle?.confidence
    })).filter(d => d.y !== undefined && d.confidence > 0.5);
    
    if (ankleData.length === 0) {
      console.warn('No valid ankle keypoints found for pedal position identification');
      return frames;
    }
    
    // Find min/max Y
    const minY = Math.min(...ankleData.map(d => d.y));  // TDC (highest position on screen)
    const maxY = Math.max(...ankleData.map(d => d.y));  // BDC (lowest position on screen)
    
    console.log(`Pedal positions - TDC (minY): ${minY.toFixed(1)}, BDC (maxY): ${maxY.toFixed(1)}`);
    
    // Tag frames within threshold
    frames.forEach((frame, idx) => {
      const ankle = frame.keypoints?.rightAnkle;
      if (ankle && ankle.confidence > 0.5) {
        if (Math.abs(ankle.y - minY) <= THRESHOLD) {
          frame.pedalTag = 'TDC';
          console.log(`Frame ${idx} tagged as TDC (ankle Y: ${ankle.y.toFixed(1)})`);
        } else if (Math.abs(ankle.y - maxY) <= THRESHOLD) {
          frame.pedalTag = 'BDC';
          console.log(`Frame ${idx} tagged as BDC (ankle Y: ${ankle.y.toFixed(1)})`);
        }
      }
    });
    
    return frames;
  }

  /**
   * Analyze pedal positions and calculate angles
   */
  private analyzePedalPositions(frames: SimpleFrame[]): {
    bdc: PedalAnalysis | null;
    tdc: PedalAnalysis | null;
    trunkAngleAvg: number | null;
    trunkAngleDivergence: boolean;
    pelvicRock: PelvicRockResult | null;
  } {
    const bdcFrames = frames.filter(f => f.pedalTag === 'BDC');
    const tdcFrames = frames.filter(f => f.pedalTag === 'TDC');
    
    console.log(`Analyzing pedal positions: ${bdcFrames.length} BDC frames, ${tdcFrames.length} TDC frames`);
    
    let bdcAnalysis: PedalAnalysis | null = null;
    if (bdcFrames.length > 0) {
      try {
        bdcAnalysis = this.createPedalAnalysis('BDC', bdcFrames, frames);
      } catch (e) {
        console.warn('BDC analysis failed:', e instanceof Error ? e.message : e);
      }
    }

    let tdcAnalysis: PedalAnalysis | null = null;
    if (tdcFrames.length > 0) {
      try {
        tdcAnalysis = this.createPedalAnalysis('TDC', tdcFrames, frames);
      } catch (e) {
        console.warn('TDC analysis failed:', e instanceof Error ? e.message : e);
      }
    }

    // Compute averaged trunk angle and divergence flag
    let trunkAngleAvg: number | null = null;
    let trunkAngleDivergence = false;
    if (bdcAnalysis && tdcAnalysis) {
      const diff = Math.abs(bdcAnalysis.angles.trunk - tdcAnalysis.angles.trunk);
      trunkAngleAvg = (bdcAnalysis.angles.trunk + tdcAnalysis.angles.trunk) / 2;
      trunkAngleDivergence = diff > 5;
      console.log(`Trunk angle avg: ${trunkAngleAvg.toFixed(1)}°, divergence: ${diff.toFixed(1)}° (flag: ${trunkAngleDivergence})`);
    } else if (bdcAnalysis) {
      trunkAngleAvg = bdcAnalysis.angles.trunk;
    } else if (tdcAnalysis) {
      trunkAngleAvg = tdcAnalysis.angles.trunk;
    }

    const pelvicRock = this.computePelvicRock(frames);
    
    return { bdc: bdcAnalysis, tdc: tdcAnalysis, trunkAngleAvg, trunkAngleDivergence, pelvicRock };
  }

  /**
   * Compute pelvic rock amplitude from hip Y movement across all frames.
   *
   * Scale factor: ankle Y range ≈ pedal circle diameter ≈ 2 × 172mm crank arm = 344mm.
   * Returns null if there isn't enough reliable data.
   */
  private computePelvicRock(frames: SimpleFrame[]): PelvicRockResult | null {
    const MIN_VALID_FRAMES  = 5;
    const MIN_ANKLE_RANGE_PX = 5;
    const CRANK_DIAMETER_MM  = 344; // 2 × 172mm standard crank arm

    // Collect valid hip Y and ankle Y values
    const hipYValues: number[]   = [];
    const ankleYValues: number[] = [];

    for (const frame of frames) {
      const hip   = frame.keypoints?.rightHip;
      const ankle = frame.keypoints?.rightAnkle;
      if (hip   && hip.confidence   > 0.5) hipYValues.push(hip.y);
      if (ankle && ankle.confidence > 0.5) ankleYValues.push(ankle.y);
    }

    if (hipYValues.length < MIN_VALID_FRAMES) {
      console.warn(`Pelvic rock: not enough valid hip frames (${hipYValues.length})`);
      return { mm: null, status: 'no_data' };
    }

    const ankleRange = Math.max(...ankleYValues) - Math.min(...ankleYValues);
    if (ankleRange < MIN_ANKLE_RANGE_PX) {
      console.warn(`Pelvic rock: ankle range too small (${ankleRange.toFixed(1)}px) — cannot derive scale`);
      return { mm: null, status: 'no_data' };
    }

    const rockPx = Math.max(...hipYValues) - Math.min(...hipYValues);
    const scale  = CRANK_DIAMETER_MM / ankleRange;
    const mm     = parseFloat((rockPx * scale).toFixed(1));

    const status: PelvicRockResult['status'] =
      mm < 20  ? 'green' :
      mm <= 40 ? 'amber' :
                 'red';

    console.log(
      `Pelvic rock: ${rockPx.toFixed(1)}px × ${scale.toFixed(3)} = ${mm}mm → ${status}` +
      ` (ankle range: ${ankleRange.toFixed(1)}px, hip frames: ${hipYValues.length})`
    );

    return { mm, status };
  }

  /**
   * Create pedal analysis for a specific position
   */
  private createPedalAnalysis(
    position: 'BDC' | 'TDC',
    taggedFrames: SimpleFrame[],
    allFrames: SimpleFrame[]
  ): PedalAnalysis {
    // Import utility functions
    const { averageKeypoints, calculateKneeAngle, calculateHipAngle, calculateTrunkAngle, calculateElbowAngle, findClosestFrame } = 
      require('../utils/angleCalculations');
    
    // 1. Average keypoints
    const avgKeypoints = averageKeypoints(taggedFrames);
    console.log(`${position} averaged keypoints:`, avgKeypoints);
    
    // 2. Calculate angles
    const knee = calculateKneeAngle(avgKeypoints);
    const hip = calculateHipAngle(avgKeypoints);
    const trunk = calculateTrunkAngle(avgKeypoints);
    const elbow = calculateElbowAngle(avgKeypoints);
    console.log(`${position} angles - Knee: ${knee.toFixed(1)}°, Hip: ${hip.toFixed(1)}°, Trunk: ${trunk.toFixed(1)}°, Elbow: ${elbow.toFixed(1)}°`);
    
    // 3. Find representative frame
    const repFrameIndex = findClosestFrame(taggedFrames, avgKeypoints, allFrames);
    console.log(`${position} representative frame index: ${repFrameIndex}`);
    
    return {
      position,
      averagedKeypoints: avgKeypoints,
      angles: { knee, hip, trunk, elbow },
      representativeFrameIndex: repFrameIndex
    };
  }
}
